import React, { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPaperPlane, faXmark } from "@fortawesome/free-solid-svg-icons";
import ReactMarkdown from "react-markdown";
import rehypeSanitize from "rehype-sanitize";
import gfm from "remark-gfm";
import { CustomLink } from "../common/chat-entries";
import Chat from "./Chat";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

// Add mobile responsive styling
const mobileMediaQuery = "@media (max-width: 640px)";
const mobileStyles = {
    chatContainer: {
        width: "100%",
        maxWidth: "100%",
        right: 0,
        left: 0,
        bottom: 0,
        borderRadius: "8px 8px 0 0"
    },
    messageMaxWidth: {
        maxWidth: "85%"
    }
};

const CHATTRICK_LOGO_URL =
    "https://beta.chattrick.witniumtech.com/chattrick-logo.png";

const DEFAULT_PRIVACY_POLICY =
    "By using this chat, you agree to our [privacy policy](https://chattrick-app.witniumtech.com/privacy-policy)";

/**
 * ChatBubble - A React component that implements a floating chat interface
 *
 * This component creates an interactive chat interface that matches the provided design
 * and handles real-time communication with a chatbot server using Socket.IO.
 *
 * @component
 * @param {Object} props
 * @param {string} props.apiBaseUrl - The base URL for the chat API endpoint
 * @param {string} props.botId - Unique identifier for the chatbot instance
 * @param {string} [props.initialMessage=""] - Initial message to be sent to the bot when chat opens
 * @param {boolean} [props.initialMessageIsSilent=false] - If true, initial message won't be displayed in chat
 * @param {Array} [props.chatContext=[]] - Additional context data to be sent with chat messages
 * @param {string} [props.customDisclaimerText] - Optional custom disclaimer text for specific clients
 *
 * @state
 * - isChatOpen: boolean - Controls chat window visibility
 * - showPrivacyBanner: boolean - Controls privacy banner visibility
 *
 * @refs
 * - chatRef: References chat container for scroll management
 *
 * @events
 * - Socket Events:
 *   - connect: Updates socket state when connected
 *   - ready: Updates socket state when ready
 *   - disconnect: Resets socket state
 *   - chat-response-progress: Handles incoming message stream
 *   - chat-response-finished: Processes completed bot response
 *
 * @features
 * - Real-time message streaming
 * - Markdown rendering support
 * - Auto-scrolling chat window
 * - Privacy banner
 * - Connection status handling
 * - Initial message support
 * - Message history management
 */
// Main ChatBubble component that handles chat interface and functionality
const ChatBubble = ({
    settings,
    apiBaseUrl,
    botId,
    initialMessage = "",
    initialMessageIsSilent = false,
    chatContext = [],
    customDisclaimerText,
}) => {
    const [isChatOpen, setIsChatOpen] = useState(false);
    const [showPrivacyBanner, setShowPrivacyBanner] = useState(true);
    const chatRef = useRef(null);
    const messagesEndRef = useRef(null);
    const initialMessageSentRef = useRef(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 640);

    console.log(settings);

    // Add a resize event listener to handle mobile/desktop transitions
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 640);
        };

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    // Toggle chat window open/closed
    const toggleChat = useCallback(() => {
        const isOpening = !isChatOpen;
        setIsChatOpen(isOpening);

        // Handle body scrolling for mobile
        if (isOpening && isMobile) {
            document.body.style.overflow = 'hidden';
        } else if (!isOpening && isMobile) {
            document.body.style.overflow = '';
        }

        // Send initial message when chat opens, but only if it hasn't been sent yet
        // and initialMessage exists
        if (isOpening && !initialMessageSentRef.current && initialMessage) {
            setTimeout(() => {
                if (chatRef.current && chatRef.current.sendMessage) {
                    // Pass initialMessageIsSilent to determine if message should be shown in history
                    chatRef.current.sendMessage(
                        initialMessage,
                        !initialMessageIsSilent
                    );
                    initialMessageSentRef.current = true;
                }
            }, 500); // Small delay to ensure chat is ready
        }
    }, [isChatOpen, initialMessage, initialMessageIsSilent, isMobile]);

    // Add cleanup to release body scroll lock when component unmounts
    useEffect(() => {
        return () => {
            document.body.style.overflow = '';
        };
    }, []);

    // Function to scroll to bottom of messages
    const scrollToBottom = useCallback(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }, []);

    // Handle privacy banner dismissal
    const dismissPrivacyBanner = useCallback(() => {
        setShowPrivacyBanner(false);
        localStorage.setItem("chatPrivacyBannerDismissed", "true");
    }, []);

    // Custom small text component for disclaimers
    const CustomSmall = useCallback(
        ({ children }) => (
            <div className="mt-1">
                <span
                    style={{
                        fontSize: "0.75em",
                        color: "gray",
                        lineHeight: "1.2",
                    }}
                >
                    {children}
                </span>
            </div>
        ),
        []
    );

    // Custom renderers for chat messages
    const renderUserMessage = useCallback(
        (msg, index) => (
            <div
                key={index}
                className="flex justify-end"
                style={{ maxWidth: "100%", paddingRight: "12px" }}
            >
                <div className="max-w-[70%]" style={isMobile ? mobileStyles.messageMaxWidth : {}}>
                    <div
                        className="bg-blue-100 rounded-lg p-2"
                        style={{
                            wordBreak: "break-word",
                            overflowWrap: "break-word",
                        }}
                    >
                        <ReactMarkdown
                            remarkPlugins={[gfm]}
                            components={{ a: CustomLink, small: CustomSmall }}
                            rehypePlugins={[rehypeSanitize]}
                        >
                            {msg.message}
                        </ReactMarkdown>
                    </div>
                </div>
            </div>
        ),
        [CustomSmall, isMobile]
    );

    const renderBotMessage = useCallback(
        (msg, index) => {
            const botDisclaimerText =
                customDisclaimerText ||
                "I'm an AI chatbot. While I aim for accuracy, my responses may not always be entirely correct or up-to-date.";

            return (
                <div
                    key={index}
                    className="flex justify-start"
                    style={{ maxWidth: "100%", paddingLeft: "12px" }}
                >
                    <div className="max-w-[70%] mb-2" style={isMobile ? mobileStyles.messageMaxWidth : {}}>
                        <div className="text-gray-500 text-sm mb-1">
                            Chattrick
                        </div>
                        <div
                            className="bg-gray-100 rounded-lg p-2"
                            style={{
                                wordBreak: "break-word",
                                overflowWrap: "break-word",
                            }}
                        >
                            <ReactMarkdown
                                remarkPlugins={[gfm]}
                                components={{
                                    a: CustomLink,
                                    small: CustomSmall,
                                }}
                                rehypePlugins={[rehypeSanitize]}
                            >
                                {msg.message}
                            </ReactMarkdown>
                            <div className="text-gray-500 text-xs mt-2 pt-1 border-t border-gray-200">
                                {botDisclaimerText}
                            </div>
                        </div>
                    </div>
                </div>
            );
        },
        [CustomSmall, customDisclaimerText, isMobile]
    );

    // Custom input renderer
    const renderInput = useCallback(
        ({ value, onChange, onSend, disabled, ref }) => (
            <div
                className="redesigned-chat-footer bg-white border border-gray-300 rounded-lg px-3 mx-2 mb-3 focus:outline-none"
                style={{ 
                    display: "flex", 
                    alignItems: "center",
                    position: "sticky",
                    bottom: 0,
                    backgroundColor: "white",
                    zIndex: 10,
                    width: "calc(100% - 16px)",
                    boxSizing: "border-box"
                }}
            >
                <textarea
                    ref={ref}
                    value={value}
                    onChange={onChange}
                    placeholder={
                        settings.inputPlaceholder || "Type your message here..."
                    }
                    rows="1"
                    className="w-full py-2 border-none focus:outline-none focus:ring-0 focus:shadow-none resize-none"
                    style={{
                        minHeight: "40px",
                        maxHeight: "80px",
                        overflowY: "auto",
                    }}
                    disabled={disabled}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            if (value && value.trim()) {
                                // Only send if not empty
                                onSend();
                            }
                        }
                    }}
                    autoFocus={!disabled}
                />
                <button
                    onClick={onSend}
                    disabled={disabled}
                    className="redesigned-send-button ml-2 flex-shrink-0"
                    aria-label="Send message"
                >
                    <FontAwesomeIcon icon={faPaperPlane} />
                </button>
            </div>
        ),
        [settings.inputPlaceholder]
    );

    // Animation variants for framer-motion
    const chatVariants = useMemo(() => ({
        hidden: {
            opacity: 0,
            scale: isMobile ? 1 : 0.8,
            y: isMobile ? 50 : 20,
            transformOrigin: isMobile ? "bottom center" : "bottom right",
        },
        visible: {
            opacity: 1,
            scale: 1,
            y: 0,
            transition: {
                type: "spring",
                stiffness: 300,
                damping: 20,
            },
        },
        exit: {
            opacity: 0,
            scale: isMobile ? 1 : 0.8,
            y: isMobile ? 50 : 20,
            transition: {
                duration: 0.2,
                ease: "easeOut",
            },
        },
    }), [isMobile]);

    const buttonVariants = useMemo(() => ({
        hidden: { opacity: 0, scale: 0.8 },
        visible: {
            opacity: 1,
            scale: 1,
            transition: {
                type: "spring",
                stiffness: 400,
                damping: 20,
            },
        },
        hover: isMobile ? {} : {
            scale: 1.05,
            boxShadow: "0px 5px 15px rgba(0, 0, 0, 0.1)",
            transition: {
                type: "spring",
                stiffness: 400,
                damping: 10,
            },
        },
        tap: {
            scale: 0.95,
        },
    }), [isMobile]);

    // Custom container renderer
    const renderContainer = useCallback(
        ({ messages, currentResponse, input, chatState, chatContainerRef }) => {
            const isTyping = currentResponse.length > 0 || chatState?.busy;
            const streamedResponse = currentResponse
                ? currentResponse.split("\n").filter((line) => line.trim())
                : [];

            const disclaimerText =
                customDisclaimerText ||
                "I'm an AI chatbot. While I aim for accuracy, my responses may not always be entirely correct or up-to-date.";

            // Scroll to bottom of messages when rendered
            if (chatContainerRef.current) {
                chatContainerRef.current.scrollTop =
                    chatContainerRef.current.scrollHeight;
            }

            return (
                <div
                    className="chat_page fixed bottom-4 right-4 z-50"
                    style={{
                        width: isMobile ? "100%" : "clamp(300px, 95vw, 400px)",
                        height: "auto",
                        position: "fixed",
                        right: isMobile ? "0" : "20px",
                        left: isMobile ? "0" : "auto",
                        bottom: isMobile ? "0" : "20px",
                    }}
                >
                    <AnimatePresence>
                        {!isChatOpen && (
                            <motion.button
                                className="redesigned-chat-button bg-white rounded-full px-4 py-2 shadow-lg"
                                onClick={toggleChat}
                                aria-label="Open Chat"
                                variants={buttonVariants}
                                initial="hidden"
                                animate="visible"
                                exit="hidden"
                                whileHover="hover"
                                whileTap="tap"
                                style={{ 
                                    backgroundColor: "white",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    position: "fixed",
                                    right: "24px",
                                    bottom: "24px"
                                }}
                            >
                                <div className="redesigned-chat-button-icon">
                                    <img
                                        src={CHATTRICK_LOGO_URL}
                                        alt="Chattrick"
                                        className="w-6 h-6 rounded-full"
                                    />
                                </div>
                                <div className="redesigned-chat-button-text">
                                    <span className="ml-2">
                                        {settings.buttonText || "Chat with us"}
                                    </span>
                                </div>
                            </motion.button>
                        )}
                    </AnimatePresence>

                    <AnimatePresence>
                        {isChatOpen && (
                            <motion.div
                                className="redesigned-chat bg-white shadow-xl sm:w-full"
                                style={{
                                    right: 0,
                                    bottom: 0,
                                    left: isMobile ? 0 : "auto",
                                    position: "absolute",
                                    maxHeight: isMobile ? "100vh" : "calc(100vh - 80px)",
                                    width: isMobile ? "100vw" : "100%", 
                                    display: "flex",
                                    flexDirection: "column",
                                    borderRadius: isMobile ? "12px 12px 0 0" : "8px",
                                    overflow: "hidden"
                                }}
                                variants={chatVariants}
                                initial="hidden"
                                animate="visible"
                                exit="exit"
                            >
                                <div className="redesigned-chat-header bg-gray-300 px-5 py-3 flex justify-between items-center">
                                    <div className="redesigned-chat-info">
                                        <div
                                            className="flex flex-row items-center"
                                            style={{
                                                gap: "8px",
                                                width: "auto",
                                                justifyContent: "flex-start",
                                            }}
                                        >
                                            <img
                                                src={CHATTRICK_LOGO_URL}
                                                alt="Chattrick"
                                                className="w-10 h-10 rounded-full mr-0"
                                                style={{
                                                    marginRight: "8px",
                                                    flexShrink: 0,
                                                }}
                                            />
                                            <div
                                                className="ml-0"
                                                style={{ flexGrow: 0 }}
                                            >
                                                <div className="redesigned-chat-title text-lg font-semibold text-ellipsis overflow-hidden">
                                                    {settings.styling.title
                                                        ? settings.styling.title
                                                        : "Chattrick"}
                                                </div>
                                                <div className="redesigned-chat-subtitle text-sm text-gray-600 text-ellipsis overflow-hidden">
                                                    {settings.styling
                                                        .description
                                                        ? settings.styling
                                                              .description
                                                        : "I am here to help you"}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <motion.button
                                        onClick={toggleChat}
                                        className="redesigned-close-button flex-shrink-0 ml-2"
                                        style={{ marginLeft: "12px" }}
                                        aria-label="Close chat"
                                        whileHover={{ scale: 1.1, rotate: 180 }}
                                        whileTap={{ scale: 0.9 }}
                                        transition={{ duration: 0.2 }}
                                    >
                                        <X size={28} strokeWidth={1} />
                                    </motion.button>
                                </div>

                                <div
                                    ref={chatContainerRef}
                                    className="redesigned-chat-body bg-white"
                                    style={{
                                        overflowY: "auto",
                                        flexGrow: 1,
                                        maxHeight: isMobile ? "calc(100vh - 160px)" : "calc(70vh - 120px)",
                                        width: "100%"
                                    }}
                                >
                                    <AnimatePresence>
                                        {showPrivacyBanner && (
                                            <motion.div
                                                className="privacy-banner bg-blue-50 p-2 py-4 flex justify-between items-center text-sm border-b border-blue-200 mb-4"
                                                initial={{
                                                    opacity: 0,
                                                    height: 0,
                                                }}
                                                animate={{
                                                    opacity: 1,
                                                    height: "auto",
                                                }}
                                                exit={{ opacity: 0, height: 0 }}
                                            >
                                                <span className="px-3 pr-8">
                                                    <ReactMarkdown
                                                        remarkPlugins={[gfm]}
                                                        components={{
                                                            a: CustomLink,
                                                            small: CustomSmall,
                                                        }}
                                                        rehypePlugins={[
                                                            rehypeSanitize,
                                                        ]}
                                                    >
                                                        {settings.privacyPolicy ||
                                                            DEFAULT_PRIVACY_POLICY}
                                                    </ReactMarkdown>
                                                </span>
                                                <button
                                                    onClick={
                                                        dismissPrivacyBanner
                                                    }
                                                    className="ml-2 text-gray-500 hover:text-gray-700 hover:cursor-pointer absolute right-2 top-2"
                                                    aria-label="Dismiss privacy notice"
                                                >
                                                    <X size={16} />
                                                </button>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>

                                    <div
                                        className={`chat-messages-container ${
                                            !showPrivacyBanner ? "mt-2" : ""
                                        }`}
                                        style={{
                                            padding: "0 8px",
                                            width: "100%",
                                            boxSizing: "border-box"
                                        }}
                                    >
                                        {messages}

                                        {currentResponse &&
                                            streamedResponse.length > 0 && (
                                                <div
                                                    className="flex justify-start"
                                                    style={{
                                                        maxWidth: "100%",
                                                        paddingLeft: "12px",
                                                    }}
                                                >
                                                    <div className="max-w-[70%]" style={isMobile ? mobileStyles.messageMaxWidth : {}}>
                                                        <div className="text-gray-500 text-sm mb-1">
                                                            Chattrick
                                                        </div>
                                                        <div
                                                            className="bg-gray-100 rounded-lg p-2"
                                                            style={{
                                                                wordBreak:
                                                                    "break-word",
                                                                overflowWrap:
                                                                    "break-word",
                                                            }}
                                                        >
                                                            {streamedResponse.map(
                                                                (line, i) => (
                                                                    <ReactMarkdown
                                                                        key={i}
                                                                        remarkPlugins={[
                                                                            gfm,
                                                                        ]}
                                                                        components={{
                                                                            a: CustomLink,
                                                                            small: CustomSmall,
                                                                        }}
                                                                        rehypePlugins={[
                                                                            rehypeSanitize,
                                                                        ]}
                                                                    >
                                                                        {line}
                                                                    </ReactMarkdown>
                                                                )
                                                            )}
                                                            <div className="text-gray-500 text-sm mt-2 pt-1 border-t border-gray-200">
                                                                {disclaimerText}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                        {isTyping &&
                                            !streamedResponse.length && (
                                                <div
                                                    className="flex justify-start"
                                                    style={{
                                                        maxWidth: "100%",
                                                        paddingLeft: "12px",
                                                    }}
                                                >
                                                    <div className="max-w-[70%]" style={isMobile ? mobileStyles.messageMaxWidth : {}}>
                                                        <div className="text-gray-500 text-sm mb-1">
                                                            Chattrick
                                                        </div>
                                                        <div
                                                            className="bg-gray-100 rounded-lg p-2"
                                                            style={{
                                                                wordBreak:
                                                                    "break-word",
                                                                overflowWrap:
                                                                    "break-word",
                                                            }}
                                                        >
                                                            {settings.aiTypingText ||
                                                                "AI is typing"}
                                                            <span className="dot">
                                                                .
                                                            </span>
                                                            <span className="dot">
                                                                .
                                                            </span>
                                                            <span className="dot">
                                                                .
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        {/* Invisible element to scroll to */}
                                        <div ref={messagesEndRef} />
                                    </div>
                                </div>

                                {input}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            );
        },
        [
            isChatOpen,
            toggleChat,
            showPrivacyBanner,
            dismissPrivacyBanner,
            apiBaseUrl,
            CustomSmall,
            customDisclaimerText,
            isMobile
        ]
    );

    return (
        <Chat
            ref={chatRef}
            apiBaseUrl={apiBaseUrl}
            botId={botId}
            initialMessage=""
            initialMessageIsSilent={initialMessageIsSilent}
            chatContext={chatContext}
            persistConnection={true}
            customDisclaimerText={customDisclaimerText}
            renderUserMessage={renderUserMessage}
            renderBotMessage={renderBotMessage}
            renderInput={renderInput}
            renderContainer={renderContainer}
        />
    );
};

export default ChatBubble;
